import { Platform } from "react-native";
import { File, Paths } from "expo-file-system";
import { logAndFlush } from "./batch-logger";

/**
 * Structure of a persisted crash tombstone.
 */
export interface ICrashTombstone {
  /** Timestamp when the crash occurred */
  timestamp: number;
  /** ISO format of the crash time */
  timestampIso: string;
  /** Whether the error was flagged as fatal by the runtime */
  isFatal: boolean;
  /** The error message */
  message: string;
  /** Stack trace if available */
  stack?: string;
  /** Additional diagnostic context */
  context?: string;
  /** Current operating system */
  platform: string;
}

const getCrashTombstoneFile = (): File | null => {
  if (Platform.OS === "web") return null;
  try {
    return new File(Paths.cache, "last_crash.json");
  } catch {
    return null;
  }
};
const WEB_CRASH_STORAGE_KEY = "@memories/last_crash_tombstone";

let isInitialized = false;

/**
 * Writes a crash tombstone record to persistent storage so that it survives app termination.
 *
 * @param {any} error - The uncaught or caught error object.
 * @param {boolean} isFatal - Whether the crash terminated the runtime.
 * @param {string} [context] - Optional component or module context description.
 * @returns {Promise<void>}
 */
export async function recordCrashTombstone(
  error: any,
  isFatal: boolean = false,
  context?: string
): Promise<void> {
  const tombstone: ICrashTombstone = {
    timestamp: Date.now(),
    timestampIso: new Date().toISOString(),
    isFatal,
    message: error?.message || String(error) || "Unknown error",
    stack: error?.stack,
    context,
    platform: Platform.OS,
  };

  const payload = JSON.stringify(tombstone, null, 2);

  try {
    if (Platform.OS === "web") {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(WEB_CRASH_STORAGE_KEY, payload);
      }
    } else {
      const file = getCrashTombstoneFile();
      if (file) {
        await file.write(payload);
      }
    }
  } catch (storageErr) {
    console.warn("[CrashReporter] Failed to write crash tombstone to disk:", storageErr);
  }

  // Flush to remote log pipeline immediately
  logAndFlush(
    "error",
    `[CRASH_TOMBSTONE] isFatal=${isFatal} context=${context || "unknown"} msg=${tombstone.message}`,
    tombstone.stack
  );
}

/**
 * Checks for a previously persisted crash tombstone on startup, reports it to Google Cloud Logging,
 * and clears the tombstone file.
 *
 * @returns {Promise<void>}
 */
export async function checkAndFlushCrashTombstone(): Promise<void> {
  try {
    let rawContent: string | null = null;

    if (Platform.OS === "web") {
      if (typeof localStorage !== "undefined") {
        rawContent = localStorage.getItem(WEB_CRASH_STORAGE_KEY);
        if (rawContent) {
          localStorage.removeItem(WEB_CRASH_STORAGE_KEY);
        }
      }
    } else {
      const file = getCrashTombstoneFile();
      if (file && file.exists) {
        rawContent = await file.text();
        await file.delete();
      }
    }

    if (rawContent) {
      const parsed: ICrashTombstone = JSON.parse(rawContent);
      logAndFlush(
        "error",
        `[CrashRecovery] Recovered previous session crash from disk (time=${parsed.timestampIso}, fatal=${parsed.isFatal}, context=${parsed.context || "none"}): ${parsed.message}`,
        parsed.stack
      );
    }
  } catch (err: any) {
    console.warn("[CrashReporter] Error checking crash tombstone:", err);
  }
}

/**
 * Initializes the global crash reporter.
 * Hooks into React Native's ErrorUtils on mobile and unhandledrejection on all platforms.
 *
 * @returns {void}
 */
export function initCrashReporter(): void {
  if (isInitialized) return;
  isInitialized = true;

  // 1. Hook React Native global JS exception handler
  const globalObj = global as any;
  if (globalObj && globalObj.ErrorUtils && typeof globalObj.ErrorUtils.setGlobalHandler === "function") {
    const originalHandler = globalObj.ErrorUtils.getGlobalHandler();
    globalObj.ErrorUtils.setGlobalHandler(async (error: any, isFatal?: boolean) => {
      console.error("[CrashReporter] Global JS exception caught:", error, "isFatal:", isFatal);
      await recordCrashTombstone(error, Boolean(isFatal), "global_exception_handler");
      if (typeof originalHandler === "function") {
        originalHandler(error, isFatal);
      }
    });
  }

  // 2. Hook unhandled promise rejections on web and supported runtimes
  if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
    window.addEventListener("unhandledrejection", (event: any) => {
      const error = event?.reason || new Error("Unhandled promise rejection");
      recordCrashTombstone(error, false, "unhandledrejection");
    });
  }

  // 3. Check for previous crash on launch
  checkAndFlushCrashTombstone().catch((err) => {
    console.warn("[CrashReporter] checkAndFlushCrashTombstone failed:", err);
  });
}
